import { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FolderPlus, PackageSearch, X } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import { AddToProjectDialog, LengthInput, NumberInput, ProductPicker, UnitToggle, productName, useUnits } from "./common";
import { DEFAULT_WASTAGE, ROLL_TRIM_MM, SOLD_AS, calculate } from "./quantities";
import { formatLength, formatMoney, formatSize } from "./units";
import "../Moodboard/Moodboard.css";
import "./Architect.css";

// What a product's details fill in.
const specFrom = (details) => ({
  soldAs: details.sold_as,
  width: details.width_mm,
  height: details.height_mm,
  repeat: details.repeat_mm,
  price: details.price,
});

export function QuantityResult({ result, units, price, repeat }) {
  const { detail, quantity, unit } = result;
  if (result.missing.length) {
    const needs = { wall: "the wall's width and height", roll: "the roll width and length", panel: "the panel size" };
    return <p className="ar-muted">Enter {result.missing.map((key) => needs[key]).join(" and ")} to work this out.</p>;
  }
  const plural = unit === "sq ft" || quantity === 1 ? unit : `${unit}s`;
  return (
    <div className="ar-result">
      <p className="ar-result-figure">
        {quantity} <span>{plural}</span>
      </p>
      {result.soldAs === "area" && <p>Wall area {detail.areaSqFt} sq ft, plus wastage, rounded up to whole sq ft.</p>}
      {result.soldAs === "roll" && (
        <p>
          {detail.strips} strips of {formatLength(detail.drop, units)} each (wall height + {formatLength(ROLL_TRIM_MM, units)} trim
          {repeat ? ", rounded up to the pattern repeat" : ""}); {detail.stripsPerRoll || "under 1"} strip
          {detail.stripsPerRoll === 1 ? "" : "s"} per roll.
        </p>
      )}
      {result.soldAs === "panel" && (
        <p>
          {detail.across} across × {detail.down} down, covering {formatSize(detail.covers[0], detail.covers[1], units)}; trim the
          last row and column to fit.
        </p>
      )}
      {result.soldAs === "piece" && detail.suggestedWidth && (
        <>
          <p>
            Artwork looks right at {formatLength(detail.suggestedWidth[0], units)} – {formatLength(detail.suggestedWidth[1], units)} wide
            on this wall (about 57–75% of its width, or of the furniture below it).
          </p>
          <p>Hang it with its centre about {formatLength(detail.centreHeight, units)} above the floor.</p>
          {detail.fits === false && <p className="ar-warning">This size is larger than the wall.</p>}
        </>
      )}
      {result.notes.map((note) => (
        <p key={note} className="ar-warning">
          {note}
        </p>
      ))}
      {price != null && quantity != null && (
        <p className="ar-result-money">
          Estimate at INR {formatMoney(price)} each: <strong>INR {formatMoney(Math.round(price * quantity * 100) / 100)}</strong>{" "}
          before markup and GST
        </p>
      )}
    </div>
  );
}

export default function Calculator() {
  const [units, setUnits] = useUnits();
  const [wallW, setWallW] = useState(null);
  const [wallH, setWallH] = useState(null);
  const [soldAs, setSoldAs] = useState("area");
  const [spec, setSpec] = useState({ width: null, height: null, repeat: null, price: null });
  const [wastage, setWastage] = useState(null);
  const [product, setProduct] = useState(null);
  const [picking, setPicking] = useState(false);
  const [adding, setAdding] = useState(false);

  const result = calculate(soldAs, { wallW, wallH, ...spec, wastage: wastage ?? DEFAULT_WASTAGE[soldAs] });

  const pickProduct = async ([item]) => {
    setPicking(false);
    try {
      const { data } = await client.get(`/api/products/${item.file_id}/details`);
      const next = specFrom(data);
      setProduct({ ...item, details: data });
      setSoldAs(next.soldAs);
      setSpec({ width: next.width, height: next.height, repeat: next.repeat, price: next.price });
      setWastage(null);
    } catch {
      toast.error("Could not load that product's details.");
    }
  };

  const changeSoldAs = (key) => {
    setSoldAs(key);
    setWastage(null);
  };

  const sizeLabels = {
    roll: ["Roll width", "Roll length"],
    panel: ["Panel width", "Panel height"],
    piece: ["Artwork width", "Artwork height"],
  }[soldAs];

  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Tools</p>
          <h1>Quantity calculator</h1>
        </div>
        <UnitToggle units={units} onChange={setUnits} />
      </header>

      <div className="ar-calc">
        <section className="ar-card">
          <h2>Wall</h2>
          <div className="ar-row">
            <LengthInput label="Width" value={wallW} units={units} onChange={setWallW} />
            <LengthInput label="Height" value={wallH} units={units} onChange={setWallH} />
          </div>

          <h2>Product</h2>
          {product ? (
            <div className="ar-product-chip">
              <img src={absoluteUrl(product.thumbnail_path) || undefined} alt="" />
              <div>
                <strong>{productName(product)}</strong>
                <span>{[product.details.code && `Code ${product.details.code}`, product.details.material].filter(Boolean).join(" · ")}</span>
              </div>
              <button type="button" className="mb-icon-button" onClick={() => setProduct(null)} aria-label="Clear product" title="Clear product">
                <X size={15} strokeWidth={1.8} />
              </button>
            </div>
          ) : (
            <button type="button" className="mb-wide-button ar-fit" onClick={() => setPicking(true)}>
              <PackageSearch size={15} strokeWidth={1.8} aria-hidden="true" />
              Use a product's details
            </button>
          )}

          <div className="ar-field">
            <span className="ar-field-label">Sold as</span>
            <div className="ar-segmented ar-segmented-wrap" role="radiogroup" aria-label="Sold as">
              {SOLD_AS.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  role="radio"
                  aria-checked={soldAs === entry.key}
                  className={soldAs === entry.key ? "active" : ""}
                  onClick={() => changeSoldAs(entry.key)}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </div>

          {sizeLabels && (
            <div className="ar-row">
              <LengthInput label={sizeLabels[0]} value={spec.width} units={units} onChange={(width) => setSpec((s) => ({ ...s, width }))} />
              <LengthInput label={sizeLabels[1]} value={spec.height} units={units} onChange={(height) => setSpec((s) => ({ ...s, height }))} />
            </div>
          )}
          {soldAs === "roll" && (
            <LengthInput
              label="Pattern repeat (blank for none)"
              value={spec.repeat}
              units={units}
              onChange={(repeat) => setSpec((s) => ({ ...s, repeat }))}
              placeholder="e.g. 64 cm"
            />
          )}
          <div className="ar-row">
            {soldAs !== "piece" && (
              <NumberInput label="Extra for wastage" value={wastage ?? DEFAULT_WASTAGE[soldAs]} onChange={setWastage} max={100} suffix="%" />
            )}
            <NumberInput
              label={`Price ${SOLD_AS.find((entry) => entry.key === soldAs).per} (optional)`}
              value={spec.price}
              onChange={(price) => setSpec((s) => ({ ...s, price }))}
              suffix="INR"
            />
          </div>
        </section>

        <section className="ar-card ar-calc-result">
          <h2>You need</h2>
          <QuantityResult result={result} units={units} price={spec.price} repeat={spec.repeat} />
          {product && result.quantity != null && (
            <button type="button" className="mb-primary" onClick={() => setAdding(true)}>
              <FolderPlus size={16} strokeWidth={1.9} aria-hidden="true" />
              Add to a project
            </button>
          )}
          <p className="ar-muted ar-small">
            Figures are a guide for ordering; confirm site measurements before production.
          </p>
        </section>
      </div>

      {picking && <ProductPicker title="Use a product's details" onClose={() => setPicking(false)} onPick={pickProduct} />}
      {adding && product && (
        <AddToProjectDialog
          onClose={() => setAdding(false)}
          items={[
            {
              file_id: product.file_id,
              wall_w_mm: wallW,
              wall_h_mm: wallH,
              wastage_pct: wastage ?? undefined,
              overrides: Object.fromEntries(
                Object.entries({
                  sold_as: soldAs !== product.details.sold_as ? soldAs : undefined,
                  width_mm: spec.width !== product.details.width_mm ? spec.width : undefined,
                  height_mm: spec.height !== product.details.height_mm ? spec.height : undefined,
                  repeat_mm: spec.repeat !== product.details.repeat_mm ? spec.repeat : undefined,
                  price: spec.price !== product.details.price ? spec.price : undefined,
                }).filter(([, value]) => value !== undefined && value !== null)
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
